<template>
	<BaseModal :title="t('title')" v-model="show" noPadding @scroll="scroll">
		<div class="px-5">
			<div
				class="via-fill-tertiary to-fill-tertiary sticky -top-16 z-1 -mt-4 bg-linear-to-t from-transparent py-4"
			>
				<SearchInput :placeholder="t('search')" v-model="search" />
			</div>
			<div class="space-y-4 pb-10" v-if="!notFound">
				<div
					v-for="(box, i) in versions"
					:class="{
						'border-separator-secondary border-b border-dashed pb-4':
							i !== versions.length - 1,
						'animate-pulse': loading
					}"
				>
					<div class="flex items-center justify-between gap-4">
						<div class="text-label-tertiary truncate text-sm font-medium">
							{{ box.name }}
						</div>
						<CoinPrice :price="box.price" />
					</div>
					<div class="mt-4 flex flex-col gap-1.5">
						<CaseItemPrice
							v-for="item in box.items"
							:src="icon(item.modelUuid)"
							:name="item.modelName"
							:collection="item.collectionName"
							:price="item.price"
							:chance="item.chance"
							:preloader="item.preloader || undefined"
							v-hover="{ active: 'scale-95', impact: 'soft' }"
							@click="openTicketsModal(item)"
						/>
					</div>
					<div class="mt-4 flex items-center justify-between">
						<div class="text-label-tertiary text-sm">
							<AgoText
								v-if="box.versionCreatedAt"
								:date="new Date(box.versionCreatedAt)"
								:prefix="t('updated')"
							/>
						</div>
						<div
							class="text-label-tertiary inline-flex items-center gap-1.5 text-sm font-medium"
							v-hover="{ active: 'scale-90' }"
							@click="copy(box.version)"
						>
							<div>
								{{ t('caseId', [box.version]) }}
							</div>
							<CopyIcon class="h-5 w-5" />
						</div>
					</div>
				</div>
				<div
					class="flex items-center justify-center"
					:class="{
						'mt-10': versions.length,
						'h-[calc(50vh-40px)]': !versions.length
					}"
					v-if="loading"
				>
					<LoaderIcon class="text-label-tertiary h-6 w-6" />
				</div>
			</div>
			<PageEmpty
				:title="t('notFound.title')"
				:desc="t('notFound.desc')"
				v-if="notFound"
			/>
		</div>
		<ItemTicketsModal
			v-if="selectedItem"
			v-model="itemModal"
			v-bind="selectedItem"
		/>
	</BaseModal>
</template>
<script setup lang="ts">
import { ref, watch } from 'vue'
import BaseModal from './BaseModal.vue'
import SearchInput from '../SearchInput.vue'
import { icon } from '@/utils/urls'
import { useI18n } from 'vue-i18n'
import CaseItemPrice from '../CaseItemPrice.vue'
import { client } from '@/api/orpc'
import CoinPrice from '../CoinPrice.vue'
import AgoText from '../AgoText.vue'
import { debounce } from '@/utils/debounce'
import LoaderIcon from '@/assets/icons/loader.svg'
import CopyIcon from '@/assets/icons/copy.svg'
import { useToast } from '@/stores/toast'
import PageEmpty from '../PageEmpty.vue'
import ItemTicketsModal from './ItemTicketsModal.vue'

const { t } = useI18n({
	messages: {
		en: {
			title: 'prices history',
			search: 'Search for case ID...',
			updated: 'Updated',
			caseId: 'Case ID: {0}',
			copied: 'Copied case ID!',
			notFound: {
				title: 'Case not found',
				desc: 'Try searching for another case ID or check back later.'
			}
		},
		ru: {
			title: 'история цен',
			search: 'Поиск по ID кейса...',
			updated: 'Обновлен',
			caseId: 'ID кейса: {0}',
			copied: 'ID кейса скопирован!',
			notFound: {
				title: 'Кейс не найден',
				desc: 'Попробуй поискать другой ID кейса или зайти позже.'
			}
		}
	}
})

const props = defineProps<{
	version: number
	uuid: string
}>()

const show = defineModel<boolean>()

const search = ref('')

const versions = ref<
	Array<
		Extract<
			NonNullable<Awaited<ReturnType<typeof client.game.getCaseItems>>['data']>,
			{ type: 'regular' }
		>
	>
>([])

const loading = ref(false)
const notFound = ref(false)
const knownMissingVersions: number[] = []

const fetchMore = async (skipVersions?: number) => {
	if (loading.value && !skipVersions) {
		return
	}

	let version = props.version - 1
	let fromSearch = false
	let fromNextVersion = false

	if (versions.value.length) {
		const last = versions.value[versions.value.length - 1]

		if (last) {
			version = last.version - 1
			fromNextVersion = true
		}
	}

	if (search.value && !isNaN(parseInt(search.value))) {
		fromSearch = true
		version = parseInt(search.value)
	}

	if (version - (skipVersions || 0) <= 0) {
		loading.value = false
		return
	}

	if (
		!fromSearch &&
		fromNextVersion &&
		knownMissingVersions.includes(version - (skipVersions || 0))
	) {
		return
	}

	notFound.value = false
	loading.value = true

	const { data, error, isDefined } = await client.game.getCaseItems({
		uuid: props.uuid,
		version: version - (skipVersions || 0)
	})

	if (isDefined) {
		if (error.code === 'CASE_NOT_FOUND') {
			knownMissingVersions.push(version - (skipVersions || 0))

			if (fromSearch) {
				loading.value = false
				notFound.value = true
				versions.value = []

				return
			}

			return await fetchMore(skipVersions ? skipVersions + 1 : 1)
		}
	}

	loading.value = false

	if (error || !data) {
		return console.error(error)
	}
	if (data.type !== 'regular') {
		return
	}

	if (fromSearch) {
		versions.value = [data]
		return
	}

	versions.value.push(data)
}

const debouncedFetchMore = debounce(fetchMore, 250)

watch(search, () => {
	debouncedFetchMore()
})

fetchMore()

const toast = useToast()

const copy = async (version: number) => {
	try {
		await navigator.clipboard.writeText(version.toString())

		toast.add({
			type: 'success',
			text: t('copied')
		})
	} catch {}
}

const scroll = (e: Event) => {
	if (search.value) {
		return
	}

	const target = e.target as HTMLDivElement

	const scrollTop = target.scrollTop
	const scrollHeight = target.scrollHeight
	const clientHeight = target.clientHeight

	if (scrollHeight - clientHeight - scrollTop < 220) {
		debouncedFetchMore()
	}
}

const selectedItem = ref<{
	preloader: string | null
	collectionName: string
	modelName: string
	modelUuid: string
	price: number
	chance: number
	ticketsRange: [number, number]
}>()
const itemModal = ref(false)

const openTicketsModal = (
	item: NonNullable<typeof versions.value>[number]['items'][number]
) => {
	selectedItem.value = {
		preloader: item.preloader || null,
		collectionName: item.collectionName,
		modelName: item.modelName,
		modelUuid: item.modelUuid,
		price: item.price,
		chance: item.chance,
		ticketsRange: item.ticketsRange
	}

	itemModal.value = true
}
</script>
